<template>
  <article>
    <activity-stage
      :customRootContainerClass="`stop-drop-and-type ${keyboardEnabled ? '' : 'full-height'}`"
      :customContentContainerStyles="{ height: keyboardEnabled ? 'calc(100% - 45px)' : '100%' }"
    >
      <template slot="instructions">
        <slot name="instructions"></slot>
      </template>
      <article slot="content">
        <div style="display: flex;">
          <figure tabindex="0" class="focusable">
            <figcaption>
              <b v-text="currentRound.title"></b>
            </figcaption>
            <img
              :alt="currentRound.contentImage?.image.description"
              :src="currentRound.contentImage?.image.src"
            />
            <figcaption
              class="text"
              v-for="text of currentRound.text"
              :key="text"
              v-text="text"
            ></figcaption>
          </figure>
          <div class="steps" :class="{ isMobileView: isMobile }">
            <div id="sdtInstructions">
              <audio-toggle-button></audio-toggle-button>

              <h1>
                <b v-text="customInstructions"></b>
              </h1>
            </div>

            <text-display-dropdown
              v-for="step of currentRound.steps"
              :key="`dropdown-${step.id}`"
              :options="step.answers"
              :show-options="showOptions"
              :dropdown-id="`dd-${step.id}`"
              :selected-correct="handleCorrectSelect"
              :selected-incorrect="handleIncorrectSelect"
              :label="step.question"
              :step-index="step.stepIndex"
              :completed="completedIndexes.includes(step.stepIndex) ? true : false"
              :disable-drop-down="progress.stepIndex !== step.stepIndex ? disableDropDown : false"
            >
            </text-display-dropdown>
          </div>
        </div>
      </article>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import { TweenMax, TimelineLite } from 'gsap';

import AssetLoader from 'mixins/AssetLoader';
import ErrorLogger from 'mixins/ErrorLogger';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ActivityManager from 'mixins/ActivityManager';

import ActivityStage from 'components/activity/ActivityStage.vue';
import AudioToggleButton from 'components/buttons/AudioToggleButton';
import TextDisplayDropdown from 'components/text-displays/TextDisplayDropdown.vue';

const handleElements = elements =>
  elements.reduce((acc, e) => {
    if (e.elements) {
      acc.push(...handleElements(e.elements));
      return acc;
    }
    if (e.type === 'text') {
      acc.push(e.text);
      return acc;
    }
    return acc;
  }, []);

export default {
  mixins: [AssetLoader, TypingActivityHelper, ActivityManager, ErrorLogger],
  components: {
    ActivityStage,
    AudioToggleButton,
    TextDisplayDropdown
  },
  data() {
    return {
      activityName: 'StopDropAndType',
      customInstructions:
        'Scroll down to read the passage. Select your answer from the drop-down box. Then type your answer.',
      disableDropDown: false,
      showOptions: false,
      completedIndexes: []
    };
  },
  computed: {
    checkProgress() {
      return this.completedIndexes.length === this.currentRound.steps.length;
    }
  },
  methods: {
    getActivityState() {
      const { rounds } = this.activityData;

      // CSC - Somebody fix me please!!
      return {
        rounds: rounds.map(({ steps, mainText, ...round }) => ({
          ...round,
          text: handleElements(mainText.elements),
          steps: steps.map(({ answers, ...step }, stepIndex) => ({
            ...step,
            answers: answers.map(({ answer, isCorrect }) => ({
              text: answer,
              isValid: isCorrect,
              answer,
              isCorrect
            })),
            stepIndex
          }))
        })),
        instructions: {
          written: null,
          voice: `${this.voiceInstructionsPrefix}/stopdropandtype-${this.getRandomVoice()}-1.mp3`
        }
      };
    },
    async loadActivity() {
      const roundSetup = this.getActivityState();
      this.createRounds(roundSetup);
      await this.loadImages();
    },
    async loadImages() {
      this.rounds.forEach(({ contentImage }) => this.queueImage(contentImage.image.src));
      return this.downloadImages();
    },
    getKeyboardConfig() {
      const { keyboardType, roofs } = this.activityData.keyboard;
      return {
        keyboardType,
        roofs
      };
    },
    startActivity() {
      this.setRound();

      this.$nextTick(this.setActivityContainerHeight);
    },
    setRound() {
      this.completedIndexes = [];
      this.setupNewRound();
      this.hideKeyboard();
      this.errorLoggerStart();
      this.$nextTick(() => {
        TweenMax.to('.stop-drop-and-type', 0.5, { opacity: 1 });
      });
    },
    handleCorrectKey(attempt) {
      this.incrementTypingIndex();
      this.sndType();

      this.captureKeyboardAttemptSegment(attempt, this.isEndOfCurrentSentence);

      if (this.isEndOfCurrentSentence) {
        this.sndBell();
        this.hideKeyboard();
        this.disableDropDown = !this.disableDropDown;
        this.completedIndexes.push(this.progress.stepIndex);
        this.setCurrentStep();
      }
      if (this.checkProgress) {
        this.handleRoundEnd();
      }
    },
    handleRoundEnd() {
      const t = new TimelineLite();
      this.sndBell();
      t.delay(0.8).call(() => {
        if (this.isLastRound) {
          TweenMax.delayedCall(0.8, this.completeActivity);
        } else {
          t.delay(1)
            .to('.stop-drop-and-type', 0.8, { opacity: 0 })
            .call(this.setRound);
        }
      });
    },
    handleCorrectSelect(stepIndex) {
      if (typeof stepIndex !== 'undefined') {
        this.showKeyboard();
        this.enableKeyboard();
        this.overwriteProgressStep(stepIndex);

        const correctAnswer = this.currentRound.steps[stepIndex].answers.find(
          ({ isCorrect }) => isCorrect
        );

        this.overwriteProgressText({ currentText: correctAnswer.answer });
        this.setScreenReaderAlert(`Correct. Now type the following - ${correctAnswer.answer}`);

        this.disableDropDown = !this.disableDropDown;
      }
      // this.showOptions = !this.showOptions;
    },
    handleIncorrectSelect() {
      this.rerenderScreenReaderAlert('Incorrect, Try Again');
    }
  }
};
</script>

<style lang="scss" scoped>
div:first-of-type {
  figure {
    padding: 1%;
    margin: 1.5%;
    width: 32%;
    overflow-y: auto;
    height: 40vh;
    border: 2px solid #000;
    display: inline-block;
    text-align: left;
    figcaption {
      &:first-of-type {
        text-align: center;
        font-size: 1em;
      }
      &.text {
        padding: 1%;
      }
    }
  }
  .steps {
    padding: 3% 3% 0px 3%;
    width: 56.5%;

    section {
      margin: 7% auto;

      &:last-of-type {
        margin: 7% auto 0px auto;
      }
    }
    span:first-of-type {
      font-weight: 900;
    }
  }

  .steps.isMobileView {
    section {
      margin: 25px auto;
    }
    span:first-of-type {
      font-weight: 900;
    }
  }

  .steps.isMobileView {
    section {
      margin: 25px auto;
    }
  }

  b {
    font-family: hwtfutura-bold;
    font-size: 1em;
  }

  b {
    font-family: hwtfutura-bold;
    font-size: 1em;
  }

  #sdtInstructions {
    display: flex;

    button {
      left: -7px;
      position: relative;
      top: -7px;
    }

    h1 {
      display: inline-block;
      font-size: 16px;
      margin: 0px;
      max-width: 530px;
    }
  }
}
</style>
